import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { useCommonGameSet } from '@sorare/core/src/set';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { SeriesDailyClaimModule } from '@sorare/marketplace/src/components/commonCard/BuyPacks/SeriesDailyClaimModule';
import { WelcomePackOrb } from '@sorare/marketplace/src/components/commonCard/BuyPacks/WelcomePackOrb/index.tsx';

import { ClaimStarterPack } from '#src/components/series/ClaimStarterPack/index.tsx';
import { SeriesBoard } from '#src/components/series/SeriesBoard/index.tsx';
import { SeriesMissions } from '#src/components/series/SeriesMissions/index.tsx';

import type {
  SeriesPlayPageQuery,
  SeriesPlayPageQueryVariables,
} from './__generated__/page.graphql.ts';

const SERIES_PLAY_PAGE_QUERY = gql`
  query SeriesPlayPageQuery($sport: Sport!) {
    currentUser {
      slug
      availableVoucher(sport: $sport) {
        id
        ...WelcomePackOrb_availableVoucher
      }
      tasksTracks(sport: $sport, rarity: common) {
        id
        steps {
          id
          aasmState
          ... on ThresholdPickerTask {
            id
            replay
          }
        }
      }
      ...SeriesMissions_currentUser
      ...SeriesBoard_currentUser
      ...ClaimStarterPack_currentUser
    }
    cardSet {
      id
      currentSet {
        slug
      }
      nextSet {
        slug
      }
      ...ClaimStarterPack_cardSetRoot
      ...SeriesBoard_cardSetRoot
    }
    market {
      id
      myCommonDailyClaimTask(sport: $sport) {
        id
        name
        ...SeriesDailyClaimModule_task
      }
    }
  }
  ${ClaimStarterPack.fragments.cardSetRoot}
  ${SeriesBoard.fragments.currentUser}
  ${SeriesDailyClaimModule.fragments.task}
  ${SeriesMissions.fragments.currentUser}
  ${WelcomePackOrb.fragments.availableVoucher}
  ${SeriesBoard.fragments.cardSet}
  ${ClaimStarterPack.fragments.currentUser}
` as TypedDocumentNode<SeriesPlayPageQuery, SeriesPlayPageQueryVariables>;

export const loader = queryLoader(SERIES_PLAY_PAGE_QUERY, ({ sport }) => ({
  sport,
}));

const Grid = styled.div`
  display: grid;
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height, 0px));
  @media ${desktopAndAbove} {
    padding: var(--double-and-a-half-unit) var(--double-unit);
    gap: var(--quadruple-unit);
    grid-template-columns: auto 420px;
  }
`;

const BoardWrapper = styled.div`
  position: relative;
  display: grid;
`;

const DesktopMissionsContainer = styled(Vertical).attrs({ gap: 5 })`
  position: relative;
`;

const BottomRightButtons = styled(Vertical)`
  position: absolute;
  bottom: 100px;
  right: var(--double-unit);

  @media ${desktopAndAbove} {
    right: 0;
  }
`;

const Field = styled.div`
  background: var(--background-image) no-repeat center center / cover;
  @media ${desktopAndAbove} {
    background:
      linear-gradient(
        to bottom,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.5) 80px,
        transparent 120px,
        transparent
      ),
      linear-gradient(
        to left,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 1) 100px,
        rgba(0, 0, 0, 0.8) 400px,
        transparent 480px,
        transparent
      ),
      var(--field-image) no-repeat calc(50% - 100px) center / cover;
  }
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1;
  pointer-events: none;
`;

const SeriesPlayPage = () => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { sport } = useSportContext();
  const { data, refetch } = useQueryLoader<SeriesPlayPageQuery>();
  const { currentUser: currentUserFromContext } = useCurrentUserContext();
  const { currentUser, market, cardSet } = data;
  const { tasksTracks } = currentUser || {};
  const { steps } = tasksTracks?.at(0) || {};
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const commonGameSet = useCommonGameSet();
  const [searchParams, setSearchParams] = useSearchParams();
  const dialog = searchParams.get('dialog');
  const showConnectTaskDialog = dialog === 'connect';
  const {
    flags: { useWelcomePromoPack = false },
  } = useFeatureFlags();

  if (!currentUser || !tasksTracks) {
    return null;
  }

  const showClaimStarterPack =
    !currentUserFromContext?.anyUserSportProfile?.commonGameOnboarded ||
    !cardSet?.currentSet;

  if (showClaimStarterPack) {
    return (
      <ClaimStarterPack
        cardSetRoot={cardSet}
        refetch={refetch}
        currentUser={currentUser}
      />
    );
  }

  const dailyClaimTask = isType(market.myCommonDailyClaimTask, 'Task')
    ? market.myCommonDailyClaimTask
    : undefined;

  const isReplayInitState = steps?.every(
    step =>
      step.aasmState === ManagerProgressionTaskState.READY &&
      isType(step, 'ThresholdPickerTask') &&
      step.replay
  );
  const availableVoucher = currentUser?.availableVoucher;
  const { backgroundImage, fieldImage } =
    commonGameSet.visual.play.getSportAssets(sport);

  return (
    <Field
      style={
        {
          '--background-image': `url(${backgroundImage})`,
          '--field-image': `url(${fieldImage})`,
        } as CSSProperties
      }
    >
      {!!switcherOpen && <BackgroundOverlay />}
      <Grid>
        <BoardWrapper>
          <SeriesBoard
            currentUser={currentUser}
            cardSet={cardSet}
            refetch={refetch}
            switcherOpen={switcherOpen}
            setSwitcherOpen={setSwitcherOpen}
            showConnectTaskDialog={showConnectTaskDialog}
            onConnectTaskDialogClose={() => {
              setSearchParams(
                prev => {
                  prev.delete('dialog');
                  return prev;
                },
                { replace: true }
              );
            }}
            onConnectTaskDialogOpen={() => {
              setSearchParams({ dialog: 'connect' }, { replace: true });
            }}
          />
          {!isReplayInitState && (
            <BottomRightButtons>
              {!!(availableVoucher && useWelcomePromoPack) && (
                <WelcomePackOrb availableVoucher={availableVoucher} />
              )}
              <SeriesDailyClaimModule
                task={dailyClaimTask}
                refetch={refetch}
                onClaimed={() => {
                  refetch();
                }}
              />
            </BottomRightButtons>
          )}
        </BoardWrapper>
        {!!isDesktopAndAbove && (
          <DesktopMissionsContainer>
            <SeriesMissions
              currentUser={currentUser}
              showFeaturedCollectionTask={true}
            />
          </DesktopMissionsContainer>
        )}
        <AbsoluteNav />
      </Grid>
    </Field>
  );
};
export default SeriesPlayPage;
